import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Polyline, Polygon,  Marker, Popup, useMap  } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIconRetina from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";


const FieldMap = ({ data }) => {
    const DefaultIcon = new L.Icon({
        iconUrl: markerIcon,
        iconRetinaUrl: markerIconRetina,
        shadowUrl: markerShadow,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        tooltipAnchor: [16, -28],
        shadowSize: [41, 41]
    });

    // Set this default icon for all markers
    L.Marker.prototype.options.icon = DefaultIcon;


    // Memoize coordinates to avoid unnecessary recalculations
    const lineStringCoordinates = useMemo(() => {
        if (data?.type === "LineString" && Array.isArray(data?.coordinates) && data?.coordinates.length > 0) {
            return Array.isArray(data?.coordinates) 
                ? data?.coordinates.map(([lng, lat]) => [lat, lng]) 
                : [];
        }
    }, [data]);
    

    const polygonCoordinates = useMemo(() => {
        if (data?.type === "Polygon" && Array.isArray(data?.coordinates) && data?.coordinates.length > 0) {
            return Array.isArray(data?.coordinates[0])
                   ? data?.coordinates[0].map(([lng, lat]) => [lat, lng]) 
                   : [];
        }
        return [];
    }, [data]);
    

    const pointCoordinates = useMemo(() => {
        if (data?.type === "Point" && Array.isArray(data?.coordinates) && data.coordinates.length >= 2) {
            const [lng, lat, alt] = data.coordinates;
            return [lat, lng, alt];
        }
        return null;
    }, [data]);


    const multiPolygonCoordinates = useMemo(() => {
        if (data?.type === "MultiPolygon" && Array.isArray(data?.coordinates) && data.coordinates.length >= 0) {
            // Convert MultiPolygon coordinates to Leaflet format
            return data.coordinates.map((polygon) =>
                polygon[0].map(([lng, lat]) => [lat, lng]) // Convert each ring from [lng, lat] to [lat, lng]
            );
        }
        return [];
    }, [data]);


    // Component to adjust map bounds dynamically
    const FitBounds = ({ bounds }) => {
        const map = useMap();

        useEffect(() => {
            if (Array.isArray(bounds) && bounds.length > 0) {
                if (data?.type === "Polygon" || data?.type === "LineString" || data?.type === "MultiPolygon") {
                    map.fitBounds(bounds);
                } else if (data?.type === "Point") {
                    map.setView(bounds, 15); // Assuming `bounds` is a single [lat, lng] coordinate
                }
            }
        }, [map, bounds]);

        return null;
    };


    // Flatten all coordinates for fitting bounds
    const multiPolygonBounds = useMemo(() => {
        return multiPolygonCoordinates.flat();
    }, [multiPolygonCoordinates]);


    const getColorForPolygon = (index) => {
        const colors = ["#FF0000", "#00FF00", "#0000FF", "#FFFF00", "#FF00FF", "#00FFFF"]; // Array of colors
        return colors[index % colors.length]; // Cycle through colors
    };    


    return (
        <MapContainer center={[0, 0]} zoom={15} style={{ height: '80vh', width: "100%" }}>
            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
            />
            {data?.type === 'LineString' && lineStringCoordinates.length > 0 && (
                <>
                    <Polyline positions={lineStringCoordinates} color="blue" />
                    <FitBounds bounds={lineStringCoordinates} />
                </>
            )}
            {data?.type === 'Polygon' && polygonCoordinates.length > 0 && (
                <>
                    <Polygon
                        positions={polygonCoordinates}
                        pathOptions={{
                        color: "blue",
                        fillColor: "lightblue",
                        fillOpacity: 0.5,
                    }}/>
                    <FitBounds bounds={polygonCoordinates} />
                </>
            )}
            {data?.type === 'Point' && pointCoordinates && (
                <>
                    <Marker position={pointCoordinates}>
                        <Popup>
                        <div>
                            <strong>Point Coordinates:</strong>
                            <br />
                            Lat: {pointCoordinates[0]}, Lng: {pointCoordinates[1]}
                        </div>
                        </Popup>
                    </Marker>
                    <FitBounds bounds={pointCoordinates} />
                </>
            )}
            {data?.type === 'MultiPolygon' && multiPolygonCoordinates && (
                <>
                    {multiPolygonCoordinates.map((polygonCoords, index) => (
                        <Polygon
                            key={index}
                            positions={polygonCoords}
                            pathOptions={{
                                color: getColorForPolygon(index),
                                fillColor: getColorForPolygon(index),
                                fillOpacity: 0.5,
                            }}
                        />
                    ))}
                    {multiPolygonBounds.length > 0 && <FitBounds bounds={multiPolygonBounds} />}
                </>
            )}
        </MapContainer>
    );
    }

export default FieldMap;





// import React from "react";
// import { MapContainer, TileLayer, Polygon, Marker } from "react-leaflet";
// import L from "leaflet";
// import "leaflet/dist/leaflet.css";

// // Fix for Leaflet marker icon issue
// import markerIcon from "leaflet/dist/images/marker-icon.png";
// import markerIconRetina from "leaflet/dist/images/marker-icon-2x.png";
// import markerShadow from "leaflet/dist/images/marker-shadow.png";

// // Configure Leaflet to use the imported images for the default icon
// const DefaultIcon = new L.Icon({
//   iconUrl: markerIcon,
//   iconRetinaUrl: markerIconRetina,
//   shadowUrl: markerShadow,
//   iconSize: [25, 41],
//   iconAnchor: [12, 41],
//   popupAnchor: [1, -34],
//   tooltipAnchor: [16, -28],
//   shadowSize: [41, 41]
// });

// // Set this default icon for all markers
// L.Marker.prototype.options.icon = DefaultIcon;

// // Your JSON data (ideally, this would come from a prop or API call)
// const datastreams = [
//   {
//     "@iot.id": 33,
//     "name": "EO_MARKERS_greeningHarvest",
//     "observedArea": {
//       "type": "Polygon",
//       "coordinates": [
//         [
//           [20.826232372, 45.301184573],
//           [20.823519499, 45.302138657],
//           [20.82370724, 45.302336288],
//           [20.824288453, 45.302132019],
//           [20.826379807, 45.301387988],
//           [20.826232372, 45.301184573]
//         ]
//       ]
//     }
//   },
//   {
//     "@iot.id": 35,
//     "name": "IoTObservations_1305167563014004",
//     "observedArea": {
//       "type": "Point",
//       "coordinates": [13.519882, 49.23077]
//     }
//   },
//   // Additional datastreams here...
// ];

// const FieldMap = () => {
//   // Function to render polygons and points based on observedArea type
//   const renderShapes = () => {
//     return datastreams.map((datastream, index) => {
//       const { observedArea } = datastream;
      
//       if (observedArea.type === "Polygon") {
//         // Convert Polygon coordinates for Leaflet
//         const polygonPositions = observedArea.coordinates[0].map(coord => [coord[1], coord[0]]);
//         return <Polygon key={index} positions={polygonPositions} color="blue" />;
//       } else if (observedArea.type === "Point") {
//         // Convert Point coordinates for Leaflet
//         const [longitude, latitude] = observedArea.coordinates;
//         const position = [latitude, longitude];
//         return <Marker key={index} position={position} />;
//       }
//       return null;
//     });
//   };

//   return (
//     <MapContainer center={[45.3012, 20.825]} zoom={5} style={{ height: "600px", width: "100%" }}>
//       <TileLayer
//         url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
//         attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
//       />
//       {renderShapes()}
//     </MapContainer>
//   );
// };

// export default FieldMap;
